import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Field, reduxForm, change, getFormValues } from 'redux-form';
import { Button, ButtonToolbar } from 'reactstrap';
import moment from 'moment';
import renderSelectField from '../../../../shared/components/form/Select';
import Tooltip from '../../../../shared/components/widgets/Tooltip';
import { BUDGET_TYPE_OPTIONS, PLATFORM_BUDGET_TYPES } from '../../shared/constants';
import { renderFieldForm } from '../../shared/formComponents';
import CommonBudgetFieldsPart1 from './CommonBudgetFieldsPart1';
import ItemBudgetFields from './ItemBudgetFields';
import BrandBudgetFields from './BrandBudgetFields';
import DspBudgetFields from './DspBudgetFields';
import GoogleBudgetFields from './GoogleBudgetFields';
import MetaBudgetFields from './MetaBudgetFields';
import TikTokBudgetFields from './TikTokBudgetFields';
import CommonBudgetFields from './CommonBudgetFields';
import AmazonKeywordFields from './AmazonKeywordFields';

const GroupEditModalForm = ({
  handleSubmit,
  currentEditRow,
  activeDayOptions,
  monthOptions,
  user,
  budgetAccountData,
  disableButton,
  setShowEditModal,
  retailerConfig,
}) => {
  const selectedSeller = useSelector(store => store.sellerSelect);
  const formValues = useSelector(state => getFormValues('budgetGroupEditModal')(state));
  const {
    setupFlow,
    agencyInternal,
    budgetDriver,
    costTargetType,
    budgetAllocationSpSd,
    budgetAllocationSb,
    budgetAllocationSt,
    masterItemOnList,
    masterItemOffList,
    budgetType,
    budgetGroupName,
  } = formValues || {};

  const dispatch = useDispatch();

  // Check if this is an "- External" budget group
  const isExternalBudgetGroup = budgetGroupName && budgetGroupName.trim().endsWith('- External');

  // Get platform-specific budget type configuration
  const platformKey = retailerConfig?.key || 'amazon';
  const platformBudgetConfig = PLATFORM_BUDGET_TYPES[platformKey] || PLATFORM_BUDGET_TYPES.amazon;

  // Get platform display name for title
  const platformDisplayName = retailerConfig?.name || 'Amazon';

  // For Amazon/Walmart, filter based on "- External" rules
  // For Google/Meta/TikTok, use the platform options as-is
  let filteredBudgetTypeOptions;

  if (platformKey === 'amazon' || platformKey === 'walmart') {
    // Legacy Amazon/Walmart logic
    filteredBudgetTypeOptions = platformKey === 'amazon'
      ? BUDGET_TYPE_OPTIONS
      : BUDGET_TYPE_OPTIONS.filter(option => option.value === 'item');

    if (isExternalBudgetGroup) {
      filteredBudgetTypeOptions = filteredBudgetTypeOptions.filter(option => option.value === 'other');
    } else {
      filteredBudgetTypeOptions = filteredBudgetTypeOptions.filter(option => option.value !== 'other');
    }
  } else {
    // New platforms (Google/Meta/TikTok) use platform-specific options
    filteredBudgetTypeOptions = platformBudgetConfig.options;
  }

  // Find the selected budget type option with all metadata
  const budgetTypeOption = filteredBudgetTypeOptions.find(opt => opt.value === budgetType?.value);

  // Helper functions
  const cleanNumber = (value) => {
    if (value) {
      return value.toString().match(/^[0-9]*(?:\.\d{0,2})?/)[0];
    }
    return value;
  };

  const itemCsvInput = (value) => {
    if (value) {
      return value
        .toString()
        .trim()
        .replace(/\r/g, '')
        .replace(/\s*,\s*/g, ',')
        .replace(/\n+/g, ',');
    }
    return value;
  };

  // When budgetGroupName changes and ends with "- External", force budgetType to 'other' and includes to 'external'
  useEffect(() => {
    if (isExternalBudgetGroup) {
      // Force budget type to 'other' for External groups
      if (budgetType?.value !== 'other') {
        dispatch(change('budgetGroupEditModal', 'budgetType', { label: 'Other', value: 'other' }));
      }
      // Force includes to 'external' for External groups (only check formValues?.includes specific value)
      if (formValues?.includes?.value !== 'external') {
        dispatch(change('budgetGroupEditModal', 'includes', { label: 'Manual ADs', value: 'external' }));
      }
      // Set all budget allocations to 0 for External groups (only if they're not already 0)
      if (budgetAllocationSpSd !== 0) {
        dispatch(change('budgetGroupEditModal', 'budgetAllocationSpSd', 0));
      }
      if (budgetAllocationSb !== 0) {
        dispatch(change('budgetGroupEditModal', 'budgetAllocationSb', 0));
      }
      if (budgetAllocationSt !== 0) {
        dispatch(change('budgetGroupEditModal', 'budgetAllocationSt', 0));
      }
    }
  }, [isExternalBudgetGroup, budgetType?.value, formValues?.includes?.value, budgetAllocationSpSd, budgetAllocationSb, budgetAllocationSt, dispatch]);

  // When budgetType changes, set type-specific defaults
  useEffect(() => {
    if (budgetType?.value === 'brand') {
      dispatch(change('budgetGroupEditModal', 'portfolioIdsCsv', ''));
      dispatch(change('budgetGroupEditModal', 'budgetAllocationSpSd', 0));
      dispatch(change('budgetGroupEditModal', 'budgetAllocationSb', 100));
      dispatch(change('budgetGroupEditModal', 'budgetAllocationSt', 0));
    } else if (budgetType?.value === 'dsp') {
      dispatch(change('budgetGroupEditModal', 'portfolioIdsCsv', ''));
    }
  }, [budgetType, dispatch]);

  // Set active days from currentEditRow when editing existing budget
  // Also re-apply when setupFlow changes (fields are conditionally rendered)
  useEffect(() => {
    if (currentEditRow && setupFlow) {
      // Small delay to ensure Fields are mounted when switching from quick to advanced
      const timeoutId = setTimeout(() => {
        if (currentEditRow.active_days_csv) {
          // Existing budget group - use active_days_csv
          const activeDays = currentEditRow.active_days_csv.split(',');
          activeDays.forEach((day, pos) => {
            const activeDayObj = activeDayOptions.find(dayObj => dayObj.value === pos.toString());
            if (activeDayObj) {
              dispatch(change('budgetGroupEditModal', activeDayObj.name, day === '1'));
            }
          });
        } else {
          // New budget group - use default day values from currentEditRow (all true by default)
          activeDayOptions.forEach((dayObj) => {
            const dayValue = currentEditRow[dayObj.name];
            if (dayValue !== undefined) {
              dispatch(change('budgetGroupEditModal', dayObj.name, dayValue));
            }
          });
        }
      }, 0);

      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [currentEditRow, activeDayOptions, setupFlow, dispatch]);

  return (
    <>
      {budgetAccountData.cost_metric_view && (
        <form className="form" onSubmit={handleSubmit}>
          <h2>{platformDisplayName} Budget Groups</h2>
          <div className="form__form-group">
            <br />
          </div>

          {/* 1. Budget Group Name - ALWAYS FIRST */}
          <div className="form__form-group">
            <span className="form__form-group-label typography-message">
              Budget Group Name <Tooltip id="budgetGroupName" msg="Case ensitive and must be unique" placement="top" />
            </span>
            <div className="form__form-group-field">
              <Field
                name="budgetGroupName"
                type="text"
                component={renderFieldForm}
                required
              />
            </div>
            {isExternalBudgetGroup && (
              <div style={{ marginTop: '8px', padding: '8px', borderRadius: '4px', border: '1px solid rgba(255, 165, 0, 0.3)', backgroundColor: 'rgba(255, 165, 0, 0.08)' }}>
                <div style={{ fontSize: '0.85em', color: '#856404' }}>
                  ℹ️ Note: Budget Groups ending with <strong>&quot;- External&quot;</strong> are reserved for external ad tracking and have special restrictions applied automatically.
                </div>
              </div>
            )}
          </div>

          {/* 2. Budget Type - SECOND */}
          <div className="form__form-group">
            <span className="form__form-group-label">
              Budget Type <Tooltip id="budgetType" msg="Choose the type of budget/ad campaign you want to create" placement="top" />
            </span>
            <div className="form__form-group-field">
              <Field
                name="budgetType"
                type="text"
                component={renderSelectField}
                options={filteredBudgetTypeOptions}
                isDisabled={currentEditRow && currentEditRow.budget_group_daily_id_csv}
              />
            </div>
          </div>

          <div style={{ margin: '20px 0', height: '2px', backgroundColor: '#91BE23', width: '100%', borderRadius: '2px' }} />

          {/* Common Fields Part 1: Setup Flow through Spend Priority */}
          <CommonBudgetFieldsPart1
            setupFlow={setupFlow}
            agencyInternal={agencyInternal}
            costTargetType={costTargetType}
            formValues={formValues}
            activeDayOptions={activeDayOptions}
            user={user}
            retailerConfig={retailerConfig}
            isExternalBudgetGroup={isExternalBudgetGroup}
          />

          {/* Type-Specific Fields (Brand/DSP) */}
          {budgetType?.value === 'brand' && (
            <BrandBudgetFields
              setupFlow={setupFlow}
              agencyInternal={agencyInternal}
              masterItemOnList={masterItemOnList}
              currentEditRow={currentEditRow}
              selectedSeller={selectedSeller}
              user={user}
              dispatch={dispatch}
              change={change}
              itemCsvInput={itemCsvInput}
            />
          )}

          {budgetType?.value === 'dsp' && (
            <DspBudgetFields
              setupFlow={setupFlow}
              agencyInternal={agencyInternal}
              masterItemOnList={masterItemOnList}
              masterItemOffList={masterItemOffList}
              itemCsvInput={itemCsvInput}
            />
          )}

          {/* Google Budget Fields - All 3 Google ad types */}
          {platformKey === 'google' && budgetTypeOption && (
            <GoogleBudgetFields
              budgetTypeOption={budgetTypeOption}
              setupFlow={setupFlow}
              agencyInternal={agencyInternal}
              formValues={formValues}
              itemCsvInput={itemCsvInput}
            />
          )}

          {/* Meta Budget Fields - All 3 Meta ad types */}
          {platformKey === 'meta' && budgetTypeOption && (
            <MetaBudgetFields
              budgetTypeOption={budgetTypeOption}
              setupFlow={setupFlow}
              agencyInternal={agencyInternal}
              formValues={formValues}
            />
          )}

          {/* TikTok Budget Fields - All 3 TikTok ad types */}
          {platformKey === 'tiktok' && budgetTypeOption && (
            <TikTokBudgetFields
              budgetTypeOption={budgetTypeOption}
              setupFlow={setupFlow}
              agencyInternal={agencyInternal}
              formValues={formValues}
              itemCsvInput={itemCsvInput}
            />
          )}

          {/* Item Budget Fields (includes Item Type, Item Selection, Destination, ON/OFF lists, Split) */}
          {budgetType?.value === 'item' && platformKey !== 'google' && (
            <ItemBudgetFields
              setupFlow={setupFlow}
              agencyInternal={agencyInternal}
              masterItemOnList={masterItemOnList}
              masterItemOffList={masterItemOffList}
              currentEditRow={currentEditRow}
              retailerConfig={retailerConfig}
              selectedSeller={selectedSeller}
              itemCsvInput={itemCsvInput}
            />
          )}

          {/* Other Budget Fields (same as Item fields but hide ON/OFF lists) */}
          {budgetType?.value === 'other' && (
            <ItemBudgetFields
              setupFlow={setupFlow}
              agencyInternal={agencyInternal}
              masterItemOnList={masterItemOnList}
              masterItemOffList={masterItemOffList}
              currentEditRow={currentEditRow}
              retailerConfig={retailerConfig}
              selectedSeller={selectedSeller}
              itemCsvInput={itemCsvInput}
              isExternalBudgetGroup={isExternalBudgetGroup}
            />
          )}

          {/* Amazon Keyword Fields - Negative & Custom Keywords (all Amazon budget types) */}
          {platformKey === 'amazon' && (
            <AmazonKeywordFields
              itemCsvInput={itemCsvInput}
              formValues={formValues}
            />
          )}

          {/* Common Fields Part 2: Dates, Budget Driver, Amounts, State */}
          <CommonBudgetFields
            setupFlow={setupFlow}
            agencyInternal={agencyInternal}
            budgetDriver={budgetDriver}
            budgetType={budgetType}
            budgetAllocationSpSd={budgetAllocationSpSd}
            budgetAllocationSb={budgetAllocationSb}
            budgetAllocationSt={budgetAllocationSt}
            monthOptions={monthOptions}
            selectedSeller={selectedSeller}
            dispatch={dispatch}
            change={change}
            cleanNumber={cleanNumber}
            isExternalBudgetGroup={isExternalBudgetGroup}
            retailerConfig={retailerConfig}
          />

          <ButtonToolbar className="form__button-toolbar">
            <Button color="primary" type="submit" disabled={disableButton}>
              {!(currentEditRow && currentEditRow.budget_group_daily_id_csv) ? 'Add' : 'Edit'}
            </Button>
            <Button type="button" onClick={() => setShowEditModal(false)}>Cancel</Button>
          </ButtonToolbar>
        </form>
      )}
      {!budgetAccountData.cost_metric_view && budgetAccountData.cost_metric_view !== 'roas' && (
        <div>
          <p>Budget Account Settings are required before you can manage Budget Groups</p>
          <br />
          <br />
          <Button type="button" onClick={() => setShowEditModal(false)}>Ok</Button>
        </div>
      )}
    </>
  );
};

GroupEditModalForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  currentEditRow: PropTypes.shape(),
  activeDayOptions: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  monthOptions: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  user: PropTypes.shape().isRequired,
  budgetAccountData: PropTypes.shape().isRequired,
  disableButton: PropTypes.bool.isRequired,
  setShowEditModal: PropTypes.func.isRequired,
  retailerConfig: PropTypes.shape().isRequired,
};

GroupEditModalForm.defaultProps = {
  currentEditRow: null,
};

const validate = (values) => {
  const errors = {};

  // Budget Group Name validation
  if (!values.budgetGroupName) {
    errors.budgetGroupName = 'Name field cannot be empty';
  } else if (values && values.budgetGroupName.length > 255) {
    errors.budgetGroupName = 'Max length is 255 characters';
  } else if (values.budgetGroupName.replace(/[^\-0-9A-Za-z ]/g, '').length !== values.budgetGroupName.length) {
    errors.budgetGroupName = 'Only alphanumeric, spaces, and hyphons charactors are allowed';
  }

  // Days of the week validation
  if (!values.monday && !values.tuesday && !values.wednesday && !values.thursday && !values.friday && !values.saturday && !values.sunday) {
    errors.daysOfTheWeek = 'At least one day of the week needs to be selected';
  }

  // Includes validation
  if (values.includes?.value === '' && !values.agencyInternal?.value) {
    errors.includes = 'Includes only allows for Limitless ADs';
  }

  // Budget type-specific validation
  const budgetType = values.budgetType?.value;
  const retailerKey = values.retailerKey || 'amazon';

  // Google budget validation
  if (retailerKey === 'google') {
    if (!values.googleLanguages || values.googleLanguages.trim() === '') {
      errors.googleLanguages = 'Language constants are required for Google budgets';
    }
  }

  if (budgetType === 'brand') {
    if (!values.brand) {
      errors.brand = 'Brand is required for brand budget type';
    }
    if (!values.brandAdFormat) {
      errors.brandAdFormat = 'Ad Format is required for brand budget type';
    }
  }

  if (budgetType === 'dsp') {
    if (!values.dspGender) {
      errors.dspGender = 'Gender targeting is required for DSP budgets';
    }
    if (!values.dspAdFormat) {
      errors.dspAdFormat = 'Ad Format is required for DSP budgets';
    }
    if (!values.dspSpendTactic) {
      errors.dspSpendTactic = 'Spend Tactic is required for DSP budgets';
    }
    if (!values.dspItemType) {
      errors.dspItemType = 'Item Type is required for DSP budgets';
    }
    if (values.masterItemOnList) {
      try {
        const url = new URL(values.masterItemOnList.trim());
        if (url.protocol !== 'http:' && url.protocol !== 'https:') {
          errors.masterItemOnList = 'Landing Page URL must start with http:// or https://';
        }
      } catch {
        errors.masterItemOnList = 'Please enter a valid URL';
      }
    } else {
      errors.masterItemOnList = 'Landing Page URL is required for DSP budgets';
    }
  }

  // Cost Target validation (ROAS values between 0.333 and 20)
  if (values.costTargetType?.value === 'blended' && (values.costTarget < 0.333 || values.costTarget > 20)) {
    errors.costTarget = 'Cost Target for ROAS must be between 0.333 and 20';
  }
  if (values.costTargetType?.value === 'bnb' && (values.costTargetBrand < 0.333 || values.costTargetBrand > 20)) {
    errors.costTargetBrand = 'Brand Cost Target for ROAS must be between 0.333 and 20';
  }
  if (values.costTargetType?.value === 'bnb' && (values.costTargetNonBrand < 0.333 || values.costTargetNonBrand > 20)) {
    errors.costTargetNonBrand = 'Non-Brand Cost Target for ROAS must be between 0.333 and 20';
  }

  // Item budget validation (also applies to 'other' type)
  if (budgetType === 'item' || budgetType === 'other') {
    if (values.splitItemsCheckbox && values.masterItemOffList && values.masterItemOffList.trim()) {
      errors.masterItemOffList = 'Cannot have OFF items when "Split Items Into Individual Budget Groups" is checked';
    }

    if (values.masterItemOnList && values.masterItemOffList) {
      const onItems = values.masterItemOnList.split(',').map(item => item.trim().toUpperCase()).filter(item => item);
      const offItems = values.masterItemOffList.split(',').map(item => item.trim().toUpperCase()).filter(item => item);
      const duplicates = onItems.filter(item => offItems.includes(item));

      if (duplicates.length > 0) {
        errors.masterItemOnList = `Items cannot be in both ON and OFF lists: ${duplicates.join(', ')}`;
        errors.masterItemOffList = `Items cannot be in both ON and OFF lists: ${duplicates.join(', ')}`;
      }
    }

    if (values.masterItemOnList) {
      const itemList = values.masterItemOnList.split(',').map(item => item.trim()).filter(item => item);

      if (retailerKey === 'amazon') {
        const hasB0Items = itemList.some(item => item?.toUpperCase().startsWith('B0') && item?.length === 10);
        if (hasB0Items && values.accountType === 1) {
          errors.masterItemOnList = 'Amazon Seller SKUs cannot start with B0';
        }
        if (!hasB0Items && values.accountType === 2) {
          errors.masterItemOnList = 'Amazon Vendor ASINs must start with B0';
        }
      } else if (retailerKey === 'walmart') {
        const hasNonNumeric = itemList.some(item => {
          const trimmedItem = item?.trim();
          return trimmedItem && !/^\d+$/.test(trimmedItem);
        });
        if (hasNonNumeric) {
          errors.masterItemOnList = 'Walmart items must be numeric only';
        }
      }
    }

    if (values.masterItemOffList) {
      const itemList = values.masterItemOffList.split(',').map(item => item.trim()).filter(item => item);

      if (retailerKey === 'amazon') {
        const hasB0Items = itemList.some(item => item?.toUpperCase().startsWith('B0') && item?.length === 10);
        if (hasB0Items && values.accountType === 1) {
          errors.masterItemOffList = 'Amazon Seller SKUs cannot start with B0';
        }
        if (!hasB0Items && values.accountType === 2) {
          errors.masterItemOffList = 'Amazon Vendor ASINs must start with B0';
        }
      } else if (retailerKey === 'walmart') {
        const hasNonNumeric = itemList.some(item => {
          const trimmedItem = item?.trim();
          return trimmedItem && !/^\d+$/.test(trimmedItem);
        });
        if (hasNonNumeric) {
          errors.masterItemOffList = 'Walmart items must be numeric only';
        }
      }
    }
  }

  // Brand budget validation
  if (budgetType === 'brand' && values.masterItemOnList) {
    const itemList = values.masterItemOnList.split(',').map(item => item.trim()).filter(item => item);
    const invalidAsins = itemList.filter(item => !item?.toUpperCase().startsWith('B0') || item?.length !== 10);

    if (invalidAsins.length > 0) {
      errors.masterItemOnList = `Brand budgets only accept ASINs (must start with B0 and be 10 characters). Invalid items: ${invalidAsins.join(', ')}`;
    } else {
      const asinCount = itemList.length;
      if (asinCount < 3) {
        errors.masterItemOnList = `Brand budgets require at least 3 ASINs (currently: ${asinCount})`;
      } else if (asinCount > 100) {
        errors.masterItemOnList = `Brand budgets cannot exceed 100 ASINs (currently: ${asinCount})`;
      }
    }
  }

  // Date validation
  // Check if editing existing budget group by looking at budgetGroupRecords OR budget_group_daily_id_csv
  // Use budget_group_daily_id_csv as the primary indicator since it's more reliable across all platforms
  const hasExistingRecords = values.budgetGroupRecords && values.budgetGroupRecords.length > 0 && values.budgetGroupRecords.some(r => r.budget_group_daily_id > 0);
  const hasBudgetGroupIds = values.budget_group_daily_id_csv && values.budget_group_daily_id_csv.trim() !== '';
  const isNewBudgetGroup = !hasExistingRecords && !hasBudgetGroupIds;
  const isExternalGroup = values.budgetGroupName && values.budgetGroupName.trim().endsWith('- External');

  // Allow past start dates for External budget groups and when editing existing budgets
  if (isNewBudgetGroup && !isExternalGroup && moment(values.startDate).startOf('day').isBefore(moment().startOf('day'))) {
    errors.startDate = 'Start Date cannot be in the past';
  }
  if (values.endDate && moment(values.startDate) >= moment(values.endDate)) {
    errors.endDate = 'End Date must be after Start Date';
  }

  // Monthly budget validation
  if (values.budgetDriver?.value === 'monthly') {
    let totalMonthlyBudget = 0;
    values.monthOptions?.forEach(month => {
      const monthValue = values[month.name];
      if (monthValue === null || monthValue === undefined || monthValue === '' || monthValue < 0) {
        errors[month.name] = 'Month budget must be set and cannot be negative';
      } else {
        totalMonthlyBudget += parseFloat(monthValue || 0);
      }
    });

    if (totalMonthlyBudget <= 0) {
      errors.monthlyBudgetTotal = 'At least one month must have a budget greater than 0';
    }
  }

  // Daily budget validation
  if (values.budgetDriver?.value === 'daily') {
    if (hasExistingRecords) {
      if (values.baseDailyBudgetAmount === null || values.baseDailyBudgetAmount === undefined || values.baseDailyBudgetAmount === '' || values.baseDailyBudgetAmount < 0) {
        errors.baseDailyBudgetAmount = 'Daily budget cannot be negative';
      }
    } else if (!values.baseDailyBudgetAmount || values.baseDailyBudgetAmount <= 0) {
      errors.baseDailyBudgetAmount = 'Daily budget must be set and greater than 0';
    }
  }

  return errors;
};

export default reduxForm({
  form: 'budgetGroupEditModal',
  validate,
  enableReinitialize: true, // Allow form to reinitialize when editing existing budget
})(GroupEditModalForm);
